import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as config from '@/lib-backend/methods/config';
import { ProviderStatus, RealmConfig, RealmSummary } from '@/lib-backend';

export interface RealmWithStatus {
    config: RealmConfig;
    providerStatus: ProviderStatus;
}

export type SyncProviderResult =
    | { kind: 'ok'; providerStatus: ProviderStatus }
    | { kind: 'error'; reason: 'NotFound' | 'Unknown' };

@Injectable({ providedIn: 'root' })
export class RealmService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly realms = signal<RealmSummary[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    async loadRealms(includeInactive = false): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(
                config.listRealms({ IncludeInactive: includeInactive })
            );
            this.realms.set(resp.Realms);
        } catch (e) {
            this.error.set('Failed to load realms.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async getRealm(realmId: string): Promise<RealmWithStatus | null> {
        try {
            const resp = await this.api.client.call(
                config.getRealmConfig({ RealmId: realmId })
            );
            if (resp.Result === 'Ok') {
                return { config: resp.Config, providerStatus: resp.ProviderStatus };
            }
            return null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async syncProvider(realmId: string): Promise<SyncProviderResult> {
        try {
            const resp = await this.api.client.call(
                config.syncRealmProvider({ RealmId: realmId })
            );
            if (resp.Result === 'Ok') {
                return { kind: 'ok', providerStatus: resp.ProviderStatus };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    async addRealm(params: config.AddRealmRequest): Promise<{ result: config.AddRealmResult; realmId: string | null }> {
        try {
            const resp = await this.api.client.call(config.addRealm(params));
            if (resp.Result === 'Ok') {
                await this.loadRealms();
                return { result: resp.Result, realmId: resp.Config.RealmId };
            }
            return { result: resp.Result, realmId: null };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown', realmId: null };
        }
    }

    async updateRealm(params: config.UpdateRealmConfigRequest): Promise<config.UpdateRealmConfigResult> {
        try {
            const resp = await this.api.client.call(config.updateRealmConfig(params));
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }

    async deactivate(realmId: string): Promise<config.DeactivateRealmConfigResult> {
        try {
            const resp = await this.api.client.call(
                config.deactivateRealmConfig({ RealmId: realmId })
            );
            if (resp.Result === 'Ok') {
                await this.loadRealms();
            }
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }
}
